import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { GroupChildrenState } from "@design-system/ViewTable/grouping/groupByRowTypes";
import { accountItemRowId } from "./integrationsRowId";
import { PendingAssignment } from "./OrganizationIntegrationsContext";

export const NOTHING_TO_ASSIGN_REASON =
    "None of the selected accounts are waiting to be assigned.";

export const NOTHING_TO_UPDATE_REASON =
    "None of the selected accounts can have their entity updated.";

export const ALREADY_ASSIGNED_CLIENT_REASON =
    "Some selected accounts already have a client, which can't be changed.";

const MISSING_CLIENT_REASON = "Pick a client for every selected account first.";

const MIXED_CLIENTS_REASON =
    "The selected accounts belong to different clients.";

interface SelectedAssignmentTargetsParams {
    /** Selected row ids, as the table stores them (`item:<id>` for items). */
    rowIds: string[];
    childrenByKey: Record<
        string,
        GroupChildrenState<OrganizationIntegrationAccountItem> | undefined
    >;
    /** Clients staged in the table but not yet committed, keyed by account id. */
    pendingByAccountId: Record<number, PendingAssignment>;
}

export interface SelectedAssignmentTargets {
    /**
     * Accounts whose entity can be set or changed — assigned rows included,
     * since an entity may be swapped for another of the same client. Account
     * ids, not row ids, because that is what the assign-batch API takes; sorted
     * so the result is stable to compare.
     */
    entityAccountIds: number[];
    /** Accounts whose client is still open to change: the unassigned ones. */
    clientAccountIds: number[];
    /** Why "Update client" cannot run; `undefined` when it can. */
    clientBlockedReason: string | undefined;
    /** The one client every entity-eligible account resolves to, if there is one. */
    lockedWorkspaceId: string | null;
    /** The entity all entity-eligible accounts already share, if they share one. */
    sharedEntityId: number | null;
    /** Why "Update entity" cannot run; `undefined` when it can. */
    blockedReason: string | undefined;
}

/**
 * Works out which accounts a bulk client/entity assignment can apply to.
 *
 * The two actions have different reach. An entity can be set on an unassigned
 * account and swapped on an assigned one, so both are eligible; a client can
 * only be picked while the account has none, because moving an account between
 * clients is impossible. "Update entity" additionally needs every eligible
 * account to sit under the same client — the entity options are scoped to it,
 * hence the separate `lockedWorkspaceId`. A row's client is the one it is
 * assigned into, or the one staged for it while it is still unassigned.
 *
 * Rows are resolved out of `childrenByKey`; ids that no longer resolve (their
 * group was collapsed or paged away) are dropped rather than treated as errors.
 */
export const selectedAssignmentTargets = ({
    rowIds,
    childrenByKey,
    pendingByAccountId,
}: SelectedAssignmentTargetsParams): SelectedAssignmentTargets => {
    const selectedIds = new Set(rowIds);
    const itemsById = new Map<string, OrganizationIntegrationAccountItem>();
    for (const state of Object.values(childrenByKey)) {
        for (const item of state?.items ?? []) {
            itemsById.set(accountItemRowId(item), item);
        }
    }

    // Keyed by account id: the same financial account can be selected through
    // more than one connection (two rows), but an assignment applies to the
    // account once, so the action counts distinct accounts.
    const entityAccountIdSet = new Set<number>();
    const clientAccountIdSet = new Set<number>();
    const workspaceIds = new Set<string>();
    const entityIds = new Set<number>();
    let hasClientlessAccount = false;
    let hasUnassignedAccount = false;
    let hasAssignedAccount = false;

    for (const rowId of selectedIds) {
        const item = itemsById.get(rowId);
        if (!item) {
            continue;
        }
        if (item.account.entityId !== null) {
            hasAssignedAccount = true;
        }
        // An assigned account whose client the caller can't see is left out of
        // both actions: its cells are read-only for the same reason.
        if (item.account.entityId !== null && !item.account.entity) {
            continue;
        }

        const { account } = item;
        entityAccountIdSet.add(account.id);
        if (account.entityId === null) {
            clientAccountIdSet.add(account.id);
            hasUnassignedAccount = true;
        }

        const workspaceId =
            account.entity?.workspaceId ??
            pendingByAccountId[account.id]?.workspaceId;
        if (workspaceId === undefined) {
            hasClientlessAccount = true;
        } else {
            workspaceIds.add(workspaceId);
        }
        if (account.entityId !== null) {
            entityIds.add(account.entityId);
        }
    }

    const entityAccountIds = [...entityAccountIdSet].sort((a, b) => a - b);
    const clientAccountIds = [...clientAccountIdSet].sort((a, b) => a - b);

    const [onlyWorkspaceId] = [...workspaceIds];
    const [onlyEntityId] = [...entityIds];
    const isLockedToOneClient =
        entityAccountIds.length > 0 &&
        !hasClientlessAccount &&
        workspaceIds.size === 1;

    return {
        entityAccountIds,
        clientAccountIds,
        clientBlockedReason: clientBlockedReasonFor({
            clientAccountCount: clientAccountIds.length,
            hasAssignedAccount,
        }),
        lockedWorkspaceId: isLockedToOneClient ? onlyWorkspaceId : null,
        sharedEntityId:
            !hasUnassignedAccount && entityIds.size === 1 ? onlyEntityId : null,
        blockedReason: blockedReasonFor({
            entityAccountCount: entityAccountIds.length,
            hasClientlessAccount,
            clientCount: workspaceIds.size,
        }),
    };
};

/**
 * A client is picked for the whole selection at once, so one already-assigned
 * account blocks the action rather than being quietly skipped — otherwise the
 * button would report a count that silently excludes rows the user selected.
 */
const clientBlockedReasonFor = ({
    clientAccountCount,
    hasAssignedAccount,
}: {
    clientAccountCount: number;
    hasAssignedAccount: boolean;
}): string | undefined => {
    if (hasAssignedAccount) {
        return ALREADY_ASSIGNED_CLIENT_REASON;
    }
    if (clientAccountCount === 0) {
        return NOTHING_TO_ASSIGN_REASON;
    }
    return undefined;
};

const blockedReasonFor = ({
    entityAccountCount,
    hasClientlessAccount,
    clientCount,
}: {
    entityAccountCount: number;
    hasClientlessAccount: boolean;
    clientCount: number;
}): string | undefined => {
    if (entityAccountCount === 0) {
        return NOTHING_TO_UPDATE_REASON;
    }
    if (hasClientlessAccount) {
        return MISSING_CLIENT_REASON;
    }
    if (clientCount > 1) {
        return MIXED_CLIENTS_REASON;
    }
    return undefined;
};
